import * as THREE from "three/webgpu";
import type { ResourceRegistry } from "../core/ResourceRegistry";

const GLOW_TEXTURE_SIZE = 128;
const GLOW_COLOR_INTENSITY = 1.35;

export const STELLAR_GLOW_SCALE = 1.28;

function clamp01(value: number) {
  return Math.max(0, Math.min(value, 1));
}

function smootherstep01(value: number) {
  const t = clamp01(value);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

export function stellarGlowAlphaAtRadius(radius: number) {
  const r = clamp01(radius);
  const edgeEnvelope = 1 - smootherstep01(r);
  const gaussianBody = Math.exp(-1.7 * r * r);
  return edgeEnvelope * gaussianBody;
}

export function createStellarGlowTexture(resources: ResourceRegistry) {
  const data = new Uint8Array(GLOW_TEXTURE_SIZE * GLOW_TEXTURE_SIZE * 4);

  for (let y = 0; y < GLOW_TEXTURE_SIZE; y += 1) {
    for (let x = 0; x < GLOW_TEXTURE_SIZE; x += 1) {
      const nx = ((x + 0.5) / GLOW_TEXTURE_SIZE) * 2 - 1;
      const ny = ((y + 0.5) / GLOW_TEXTURE_SIZE) * 2 - 1;
      const radius = Math.hypot(nx, ny);
      const alpha = radius >= 1 ? 0 : stellarGlowAlphaAtRadius(radius);
      const offset = (y * GLOW_TEXTURE_SIZE + x) * 4;
      data[offset] = 255;
      data[offset + 1] = 255;
      data[offset + 2] = 255;
      data[offset + 3] = Math.round(alpha * 255);
    }
  }

  const texture = new THREE.DataTexture(
    data,
    GLOW_TEXTURE_SIZE,
    GLOW_TEXTURE_SIZE,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );
  texture.name = "K·NEXUS stellar radial glow";
  texture.colorSpace = THREE.NoColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipMapLinearFilter;
  texture.generateMipmaps = true;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return resources.texture(texture);
}

export function createStellarGlow(
  resources: ResourceRegistry,
  texture: THREE.Texture,
  color: string,
  opacity: number,
  scale: number,
) {
  const material = resources.material(
    new THREE.SpriteNodeMaterial({
      color: new THREE.Color(color).multiplyScalar(GLOW_COLOR_INTENSITY),
      map: texture,
      opacity,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    }),
  );
  const glow = new THREE.Sprite(material);
  glow.scale.setScalar(scale * STELLAR_GLOW_SCALE);
  glow.userData.decorative = true;
  return { glow, material };
}
